/*
 * @providesModule PrizeEmitter
 *
 */

import React from 'react';

import AssetHelper from 'AssetHelper';
import I18n from 'INTL';
import ParticleCell from 'react-native-particle-system/ParticleCell';
import ParticleView from 'react-native-particle-system/ParticleView';
import PropTypes from 'prop-types';
import Sizes from 'Sizes';
import SkillzStyle from 'SkillzStyle';
import SKZConstants from 'SKZConstants';

import {Dimensions, Image, NativeModules, Platform, Vibration, View} from 'react-native';

const SkillzModule = NativeModules.SkillzModule;

interface BillImage {
  frontURI: string;
  backURI: string;
}

type PrizeEmitterProps = {
  amountWon: number;
  isCashWin: boolean;
  shouldVibrate: boolean;
  shouldShowPrizeRain: boolean;
  style: any;
  tierBadgeImage: string;
  children: React.ReactNode;
};

type PrizeEmitterState = {
  playedVibrate: boolean;
};

type VibrateOptions = {
  pause: number;
  remaining: number;
};

export default class PrizeEmitter extends React.Component<PrizeEmitterProps, PrizeEmitterState> {
  billFrontURI: string;
  billBackURI: string;
  practiceCurrencyURI: string;
  window: any;
  cashParticleValues: {
    name: string;
    enabled: boolean;
    contentsRect: {x: number; y: number; width: number; height: number};
    contentsScale: any;
    magnificationFilter: string;
    minificationFilter: string;
    minificationFilterBias: number;
    scale: number;
    scaleRange: number;
    scaleSpeed: number;
    color: string;
    redRange: number;
    greenRange: number;
    blueRange: number;
    alphaRange: number;
    redSpeed: number;
    greenSpeed: number;
    blueSpeed: number;
    alphaSpeed: number;
    lifetime?: number;
    lifetimeRange?: number;
    birthRate: number;
    velocity: number;
    velocityRange: number;
    xAcceleration: number;
    yAcceleration: number;
    zAcceleration: number;
    // these values are in radians, in the UI they are in degrees
    spin: number;
    spinRange: number;
    emissionLatitude: number;
    emissionLongitude: number;
    emissionRange: number;
  };
  zParticleValues: {
    name: string;
    enabled: boolean;
    contentsRect: {x: number; y: number; width: number; height: number};
    magnificationFilter: string;
    minificationFilter: string;
    minificationFilterBias: number;
    scale: number;
    scaleRange: number;
    scaleSpeed: number;
    color: string;
    redRange: number;
    greenRange: number;
    blueRange: number;
    alphaRange: number;
    redSpeed: number;
    greenSpeed: number;
    blueSpeed: number;
    alphaSpeed: number;
    lifetime?: number;
    lifetimeRange?: number;
    birthRate: number;
    velocity: number;
    velocityRange: number;
    xAcceleration: number;
    yAcceleration: number;
    zAcceleration: number;
    // these values are in radians, in the UI they are in degrees
    spin: number;
    spinRange: number;
    emissionLatitude: number;
    emissionLongitude: number;
    emissionRange: number;
  };

  constructor(props: PrizeEmitterProps) {
    super(props);

    this.state = {
      playedVibrate: false,
      shouldRender: false,
    };

    const {frontURI, backURI} = this._getBillImagesURI();
    this.billFrontURI = frontURI;
    this.billBackURI = backURI;
    this.practiceCurrencyURI = AssetHelper._urlForRemoteThemeImage(
      SkillzStyle.currencyValues.practiceCurrencyFullIconLargeURI
    );
    console.debug(
      'this.billFrontURI: ' +
        this.billFrontURI +
        ' this.billBackURI ' +
        this.billBackURI +
        ' practiceCurrencyURI ' +
        this.practiceCurrencyURI
    );
    this.window = Dimensions.get('window');
    console.log('this.window: ' + JSON.stringify(this.window));
    const scale = Sizes.dynamic({
      sm: this.window.scale * 1.5,
      xlg: this.window.scale,
      xxlg: this.window.scale == 1 ? 2 : this.window.scale,
    });

    this.cashParticleValues = {
      name: 'cash',
      enabled: true,
      contentsRect: {x: 0.0, y: 0.0, width: 1.0, height: 1.0},
      contentsScale: scale,
      magnificationFilter: 'linear',
      minificationFilter: 'linear',
      minificationFilterBias: 0.0,
      scale: 0.5,
      scaleRange: Platform.OS === 'web' ? 0.005 : 0.01,
      scaleSpeed: Platform.OS === 'web' ? 0.05 : 0.1,
      color: '#FFFFFF',
      redRange: 0.0,
      greenRange: 0.0,
      blueRange: 0.0,
      alphaRange: 1.0,
      redSpeed: 0.0,
      greenSpeed: 0.0,
      blueSpeed: 0.0,
      alphaSpeed: 100.0,

      lifetime: Platform.OS === 'web' && !this.props.tierBadgeImage ? undefined : 14000.0,
      lifetimeRange: 1.0,
      birthRate:
        Platform.OS === 'web'
          ? Sizes.dynamic({
              phone: this.props.tierBadgeImage
                ? this._getBirthRate() / 3
                : this._getBirthRate() / 5,
              tablet: this.props.tierBadgeImage
                ? this._getBirthRate() * 5
                : this._getBirthRate() / 2,
            })
          : this._getBirthRate(),
      velocity: Platform.OS === 'web' ? 30.0 : 150.0,
      velocityRange: Platform.OS === 'web' ? 5.0 : 25.0,
      xAcceleration: 0.0,
      yAcceleration: 40.0,
      zAcceleration: 0.0,

      // these values are in radians, in the UI they are in degrees
      spin: 0.0,
      spinRange: 1.047,
      emissionLatitude: 1.047,
      emissionLongitude: 0.0,
      emissionRange: 6.283,
    };

    this.zParticleValues = {
      name: 'practice',
      enabled: true,
      contentsRect: {x: 0.0, y: 0.0, width: 1.0, height: 1.0},
      magnificationFilter: 'linear',
      minificationFilter: 'linear',
      minificationFilterBias: 0.0,
      scale: 0.4,
      scaleRange: 0.0,
      scaleSpeed: 0.1,
      color: '#FFFFFF',
      redRange: 0.0,
      greenRange: 0.0,
      blueRange: 0.0,
      alphaRange: 1.0,
      redSpeed: 0.0,
      greenSpeed: 0.0,
      blueSpeed: 0.0,
      alphaSpeed: 10.0,
      lifetime: Platform.OS === 'web' && !this.props.tierBadgeImage ? undefined : 14000.0,
      lifetimeRange: 1.0,
      birthRate:
        Platform.OS === 'web'
          ? Sizes.dynamic({
              phone: this.props.tierBadgeImage
                ? this._getBirthRate() / 3
                : this._getBirthRate() / 5,
              tablet: this.props.tierBadgeImage
                ? this._getBirthRate() * 5
                : this._getBirthRate() / 2,
            })
          : this._getBirthRate(),
      velocity: 150.0,
      velocityRange: 25.0,
      xAcceleration: 0.0,
      yAcceleration: 40.0,
      zAcceleration: 0.0,

      // these values are in radians, in the UI they are in degrees
      spin: 0.0,
      spinRange: 4.084,
      emissionLatitude: 0.0,
      emissionLongitude: 0.0,
      emissionRange: 6.283,
    };


    // setTimeout(() => {
    //   this.setState({
    //     shouldRender: true,
    //   });
    // }, SKZConstants.MS_PER_SEC);
  }

  _playVibrateWithOptions(options: VibrateOptions) {
    if (options.remaining > 0) {
      Vibration.vibrate();
      setTimeout(() => {
        this._playVibrateWithOptions({pause: options.pause, remaining: options.remaining - 1});
      }, 400 + options.pause);
    }
  }

  _playVibrateForWin() {
    if (!this.state.playedVibrate) {
      this.setState({
        playedVibrate: true,
      });
      SkillzModule.playWinSound();
      if (this.props.isCashWin && this.props.amountWon) {
        if (this.props.amountWon <= 2) {
          this._playVibrateWithOptions({pause: 300, remaining: 2});
        } else if (this.props.amountWon <= 7) {
          this._playVibrateWithOptions({pause: 300, remaining: 3});
        } else if (this.props.amountWon < 20) {
          this._playVibrateWithOptions({pause: 300, remaining: 4});
        } else {
          this._playVibrateWithOptions({pause: 300, remaining: 5});
        }
      } else {
        if (this.props.amountWon >= 10) {
          Vibration.vibrate();
        }
      }
    }
  }

  _getBirthRate(): number {
    if ((this.props.isCashWin || this.props.tierBadgeImage) && this.props.amountWon) {
      if (this.props.amountWon <= 2) {
        return 1;
      } else if (this.props.amountWon <= 7) {
        return 2.5;
      } else if (this.props.amountWon < 20) {
        return 5;
      }
      return 10;
    }
    if (this.props.amountWon < 10) {
      return 5;
    }
    return 10;
  }

  _getSeed(): number {
    return Math.random() * 1000000;
  }

  _getBillImagesURI(): BillImage {
    const currentCurrency = I18n.currentCurrency();
    switch (currentCurrency) {
      case 'INR':
        return {
          frontURI: AssetHelper._urlForRemoteSourceImage('ic_bill_front_skz_inr.png'),
          backURI: AssetHelper._urlForRemoteSourceImage('ic_bill_front_skz_inr.png'),
        };
      default:
        return {
          frontURI: AssetHelper._urlForRemoteSourceImage('ic_bill_front_skz.png'),
          backURI: AssetHelper._urlForRemoteSourceImage('ic_bill_back_skz.png'),
        };
    }
  }

  _renderParticleCells(): Array<React.ReactNode> {
    const particles = [];
    let sizeMultiplier = Sizes.dynamic({
      sm: 1,
      xxlg: this.window.scale == 1 ? 2 : 1,
    });

    if (this.props.isCashWin) {
      if (Platform.OS === 'web') {
        sizeMultiplier /= this.props.tierBadgeImage ? 16 : 6;
      }
      particles.push(
        <ParticleCell {...this.cashParticleValues} style={{borderWidth: 0}} key={'bill_front'}>
          <Image
            source={{uri: this.billFrontURI}}
            style={{width: 559 * sizeMultiplier, height: 254 * sizeMultiplier}}
            resizeMode={'contain'}
          />
        </ParticleCell>
      );
      if (!this.props.tierBadgeImage) {
        particles.push(
          <ParticleCell {...this.cashParticleValues} style={{borderWidth: 0}} key={'bill_back'}>
            <Image
              source={{uri: this.billBackURI}}
              style={{width: 557 * sizeMultiplier, height: 245 * sizeMultiplier}}
              resizeMode={'contain'}
            />
          </ParticleCell>
        );
      }
      if (this.props.tierBadgeImage) {
        particles.push(
          <ParticleCell {...this.cashParticleValues} style={{borderWidth: 0}} key={'tier-badge'}>
            <Image
              source={{uri: this.props.tierBadgeImage}}
              style={{width: 40 * sizeMultiplier, height: 40 * sizeMultiplier}}
              resizeMode={'contain'}
            />
          </ParticleCell>
        );
      }
    } else {
      particles.push(
        <ParticleCell {...this.zParticleValues} style={{borderWidth: 0}} key={'practice-currency'}>
          <Image
            source={{uri: this.practiceCurrencyURI}}
            style={{width: 40 * sizeMultiplier, height: 40 * sizeMultiplier}}
            resizeMode={'contain'}
          />
        </ParticleCell>
      );
    }

    return particles;
  }

  _renderPrizeRain(): React.ReactNode {
    const xPos = this.window.width / 2;
    const emitterPos = this.props.isCashWin ? {x: xPos, y: -49} : {x: xPos, y: -99};
    return (
      <ParticleView
        style={{position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderWidth: 0}}
        name={'prizeEmitterLayer'}
        emitterPosition={emitterPos}
        emitterZPosition={0}
        emitterShape={'point'}
        emitterMode={'volume'}
        emitterDepth={0}
        useCurrentMediaTime={true}
        renderMode={'unordered'}
        seed={this._getSeed()}
        shouldFadeBackIn={this.props.tierBadgeImage}
      >
        {this._renderParticleCells()}
      </ParticleView>
    );
  }

  render(): React.ReactNode {
    if (this.props.shouldShowPrizeRain && this.props.amountWon) {
      if (this.props.shouldVibrate) {
        setTimeout(() => {
          this._playVibrateForWin();
        }, SKZConstants.MS_PER_SEC);
      }
      return (
        <View style={this.props.style}>
          {this._renderPrizeRain()}
          {this.props.children}
        </View>
      );
    }
    return <View style={this.props.style}>{this.props.children}</View>;
  }
}
